import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/Button";
import { Bell } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";

interface CustomPopoverProps {
  content: ReactNode;
  children: ReactNode;
}
const CustomPopover: FC<CustomPopoverProps> = ({ content, children }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      popoverRef.current &&
      !popoverRef.current.contains(event.target as Node)
    ) {
      setIsPopoverOpen(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      setIsPopoverOpen(!isPopoverOpen);
    }
  };

  useEffect(() => {
    if (isPopoverOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPopoverOpen]);

  return (
    <div className="relative">
      <div
        role="button"
        tabIndex={0}
        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
        onKeyDown={handleKeyDown}
        className="focus:outline-none"
      >
        {children}
      </div>
      {isPopoverOpen &&
        createPortal(
          <div
            ref={popoverRef}
            className="absolute right-0 z-10 mt-2 w-80 rounded-xl bg-white p-4 shadow-lg border border-gray-100"
          >
            {content}
          </div>,
          document.body,
        )}
    </div>
  );
};

const Notifications: FC = () => {
  const { t } = useTranslation();
  // load notifications heres;
  const notifications = [
    t("notification-new-message"),
    t("notification-order-shipped"),
    t("notification-friend-request"),
  ];

  return (
    <CustomPopover
      content={
        <div>
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            {t("notifications")}
          </h3>
          <ul className="divide-y divide-gray-100">
            {notifications.length > 0 ? (
              notifications.map((notification, index) => (
                <li
                  key={index}
                  className="py-3 text-sm text-gray-600 hover:bg-gray-50 px-2 rounded-lg transition-colors cursor-pointer"
                >
                  {notification}
                </li>
              ))
            ) : (
              <li className="py-3 text-sm text-gray-500 text-center">
                {t("no-new-notifications")}
              </li>
            )}
          </ul>
        </div>
      }
    >
      <Button
        variant="outline"
        className="flex  items-center justify-center rounded-full border-0 p-0 text-gray-500 hover:text-gray-700 transition-colors"
        title={t("notifications")}
      >
        <Bell className="h-5 w-5 stroke-2" />
      </Button>
    </CustomPopover>
  );
};
export default Notifications;
